import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import {
  ArrowRight,
  ArrowUpRight,
  MapPin,
  Users,
  ClipboardList,
  Building2,
  Lock,
  Send,
  Vote,
  ShieldCheck,
  BarChart3,
  Megaphone,
  Sparkles,
  CheckCircle2,
  Sun,
  Moon,
  ChevronRight,
} from "lucide-react";
import { toast } from "sonner";

export default function Landing() {
  const [wards, setWards] = useState([]);
  const [stats, setStats] = useState({
    total_wards: 0,
    total_booths: 0,
    total_surveys: 0,
    active_surveyors: 0,
  });
  const [form, setForm] = useState({
    name: "",
    party: "",
    phone: "",
    email: "",
    wards: [],
    message: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const [w, s] = await Promise.all([
          api.get("/public/wards"),
          api.get("/public/stats"),
        ]);
        setWards(w.data);
        setStats(s.data);
      } catch (e) {
        // Silent — landing page is public; show empty state instead
      }
    })();
  }, []);

  const toggleWard = (id) =>
    setForm((f) => ({
      ...f,
      wards: f.wards.includes(id)
        ? f.wards.filter((x) => x !== id)
        : [...f.wards, id],
    }));

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post("/auth/candidate-request", {
        name: form.name,
        party: form.party,
        phone: form.phone,
        email: form.email,
        wards_interested: form.wards,
        message: form.message,
      });
      setSubmitted(true);
      toast.success("Request submitted — admin will review and contact you.");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="lp-root">
      <style>{styles}</style>

      {/* HEADER */}
      <header className="lp-header">
        <div className="lp-container lp-header-inner">
          <a href="#top" className="lp-brand" data-testid="brand-logo">
            <span className="lp-brand-icon">
              <Vote size={18} />
            </span>
            <span className="lp-brand-text">
              Ward<span className="lp-brand-mono">Intel</span>
            </span>
          </a>
          <nav className="lp-nav">
            <a href="#how">How it works</a>
            <a href="#wards">Wards</a>
            <a href="#why">Why us</a>
          </nav>
          <div className="lp-header-cta">
            <Link to="/login" data-testid="header-login-btn" className="lp-link">
              Login
            </Link>
            <a
              href="#request"
              className="lp-btn lp-btn-saffron"
              data-testid="header-request-access-btn"
            >
              Request Access <ArrowRight size={14} />
            </a>
          </div>
        </div>
      </header>

      {/* HERO */}
      <section id="top" className="lp-hero">
        <div className="lp-hero-grain" />
        <div className="lp-container lp-hero-inner">
          <div className="lp-hero-left">
            <div className="lp-eyebrow">
              <span className="lp-dot" /> Bengaluru · BBMP Ward Intelligence ·
              v1.0
            </div>
            <h1 className="lp-h1">
              The <em>ground-truth</em>
              <br />
              ops layer for{" "}
              <span className="lp-h1-accent">winning ward elections.</span>
            </h1>
            <p className="lp-lead">
              Door-knocked. Booth-mapped. Sentiment-scored.
              <br />
              <span className="lp-lead-muted">
                We deploy surveyors block-by-block across Bengaluru and turn
                their notes into the only intelligence pack that actually
                helps you win the ward — not the headline.
              </span>
            </p>
            <div className="lp-cta-row">
              <a href="#wards" data-testid="hero-view-wards-btn" className="lp-btn lp-btn-ink">
                Explore ward intelligence <ArrowUpRight size={16} />
              </a>
              <a href="#request" data-testid="hero-request-btn" className="lp-btn lp-btn-ghost">
                Request campaign access
              </a>
            </div>
            <div className="lp-trust">
              <CheckCircle2 size={14} /> GPS-verified surveys
              <span className="lp-trust-sep" />
              <CheckCircle2 size={14} /> Booth-level sentiment
              <span className="lp-trust-sep" />
              <CheckCircle2 size={14} /> Swing voter indicators
            </div>
          </div>

          <div className="lp-hero-right">
            {/* Stat composition */}
            <div className="lp-stat-grid">
              {[
                { l: "Wards Covered", v: stats.total_wards, i: MapPin, k: "wards" },
                { l: "Booths Mapped", v: stats.total_booths, i: Building2, k: "booths" },
                { l: "Surveys Collected", v: stats.total_surveys, i: ClipboardList, k: "surveys", big: true },
                { l: "Active Surveyors", v: stats.active_surveyors, i: Users, k: "surveyors" },
              ].map((s) => (
                <div
                  key={s.l}
                  className={`lp-stat ${s.big ? "lp-stat-big" : ""}`}
                  data-testid={`stat-tile-${s.k}`}
                >
                  <div className="lp-stat-label">{s.l}</div>
                  <div className="lp-stat-value">{s.v}</div>
                  <s.i size={s.big ? 22 : 16} className="lp-stat-icon" />
                </div>
              ))}
            </div>
            <div className="lp-hero-quote">
              <Megaphone size={14} />
              <span>
                "Stop guessing your ward. The voter on door #47 just told us
                what they care about — and now you know too."
              </span>
            </div>
          </div>
        </div>
        {/* Marquee strip */}
        <div className="lp-marquee">
          <div className="lp-marquee-track">
            {[
              "Door-to-door surveys",
              "Booth-level sentiment",
              "Swing-voter indicators",
              "Language profile",
              "Issue severity index",
              "Governance perception",
              "Migrant patterns",
              "Field observer notes",
              "Voter intent",
              "Women safety",
              "Youth concerns",
            ]
              .concat([
                "Door-to-door surveys",
                "Booth-level sentiment",
                "Swing-voter indicators",
                "Language profile",
                "Issue severity index",
                "Governance perception",
              ])
              .map((t, i) => (
                <span key={i} className="lp-marquee-item">
                  <Sparkles size={11} /> {t}
                </span>
              ))}
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section id="how" className="lp-cream">
        <div className="lp-container lp-section">
          <div className="lp-section-header">
            <div className="lp-eyebrow lp-eyebrow-ink">How it works</div>
            <h2 className="lp-h2">
              Three roles. One intelligence layer.
            </h2>
          </div>
          <div className="lp-three-cols">
            {[
              {
                tag: "01 — Surveyors",
                title: "Walk the ward.",
                body: "Trained field surveyors knock on doors with a 13-section form on their phone. Every response GPS-tagged at the booth level.",
                accent: "saffron",
                icon: Users,
              },
              {
                tag: "02 — Admin",
                title: "Curate the form.",
                body: "Election strategists tune the questionnaire, manage wards & booths, and watch coverage progress in real time.",
                accent: "ink",
                icon: ShieldCheck,
              },
              {
                tag: "03 — Candidates",
                title: "Buy the truth.",
                body: "Campaign teams unlock ward-level analytics — sentiment, swing voters, language, issues — and plan precinct strategy.",
                accent: "blue",
                icon: BarChart3,
              },
            ].map((c, i) => (
              <div key={i} className={`lp-howcard lp-acc-${c.accent}`}>
                <div className="lp-howcard-tag">{c.tag}</div>
                <c.icon className="lp-howcard-icon" size={28} strokeWidth={1.6} />
                <h3 className="lp-howcard-title">{c.title}</h3>
                <p className="lp-howcard-body">{c.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* WARDS GRID */}
      <section id="wards" className="lp-ink">
        <div className="lp-container lp-section">
          <div className="lp-section-header lp-section-header-inverse">
            <div className="lp-eyebrow lp-eyebrow-saffron">Coverage map</div>
            <h2 className="lp-h2 lp-h2-inverse">
              Wards live <span className="lp-h2-strike">on</span>{" "}
              <span className="lp-saffron">on the platform</span>
            </h2>
            <p className="lp-lead-inverse">
              Each card is a real Bengaluru BBMP ward with booths mapped and
              surveys collected. Sign in or request access to open the report.
            </p>
          </div>

          {wards.length === 0 ? (
            <div className="lp-empty">No wards published yet.</div>
          ) : (
            <div className="lp-wards-grid">
              {wards.map((w, i) => (
                <div
                  key={w.id}
                  className="lp-wardcard"
                  data-testid={`landing-ward-card-${w.name.toLowerCase()}`}
                >
                  <div className="lp-wardcard-top">
                    <div>
                      <div className="lp-wardcard-num">
                        Ward #{w.number}
                      </div>
                      <div className="lp-wardcard-name">{w.name}</div>
                      <div className="lp-wardcard-district">
                        <MapPin size={11} /> {w.district}
                      </div>
                    </div>
                    <div className="lp-wardcard-index">
                      {String(i + 1).padStart(2, "0")}
                    </div>
                  </div>
                  <div className="lp-wardcard-stats">
                    <Stat label="Booths" value={w.booth_count} />
                    <Stat label="Surveys" value={w.survey_count} />
                    <Stat label="Status" value="Live" tone="ok" />
                  </div>
                  <Link
                    to="/login"
                    className="lp-wardcard-cta"
                    data-testid={`landing-view-report-${w.name.toLowerCase()}`}
                  >
                    <Lock size={13} /> View Report{" "}
                    <ChevronRight size={14} className="lp-wardcard-arrow" />
                  </Link>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* WHY US */}
      <section id="why" className="lp-cream">
        <div className="lp-container lp-section">
          <div className="lp-section-header">
            <div className="lp-eyebrow lp-eyebrow-ink">
              Why campaign managers choose us
            </div>
            <h2 className="lp-h2">
              No exit polls.
              <br />
              <span className="lp-saffron-dark">No newsroom guesswork.</span>
            </h2>
          </div>
          <div className="lp-why-grid">
            {[
              {
                k: "01",
                t: "Built for BBMP wards",
                d: "Not a national poll — every section, every question is designed for Bengaluru ward-level decision-making.",
              },
              {
                k: "02",
                t: "Live, not stale",
                d: "Forms are editable by admin in seconds. Surveyors see the new form on the next refresh. No re-deployment.",
              },
              {
                k: "03",
                t: "Booth-level granularity",
                d: "Filter analytics down to a single polling station. Spot the booths that swing the ward.",
              },
              {
                k: "04",
                t: "Pay per ward, not per seat",
                d: "Only purchase the wards your campaign actually fights in. Mocked Razorpay flow — real keys plug in cleanly.",
              },
            ].map((b) => (
              <div key={b.k} className="lp-whycard">
                <div className="lp-whycard-num">{b.k}</div>
                <div className="lp-whycard-body">
                  <h4 className="lp-whycard-title">{b.t}</h4>
                  <p className="lp-whycard-text">{b.d}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* REQUEST FORM */}
      <section id="request" className="lp-ink">
        <div className="lp-container lp-section">
          <div className="lp-request-wrap">
            <div className="lp-request-left">
              <div className="lp-eyebrow lp-eyebrow-saffron">For candidates</div>
              <h2 className="lp-h2 lp-h2-inverse">
                Tell us which wards you're fighting.
              </h2>
              <p className="lp-lead-inverse">
                Our team reviews each request manually and issues you a
                verified candidate login. You'll be able to preview every
                ward and unlock the ones that matter to your campaign.
              </p>
              <ul className="lp-request-list">
                <li><CheckCircle2 size={14} /> Manual verification — no bots</li>
                <li><CheckCircle2 size={14} /> Login issued via phone/email</li>
                <li><CheckCircle2 size={14} /> Pay per ward, download anytime</li>
              </ul>
            </div>
            <div className="lp-request-right">
              {submitted ? (
                <div className="lp-request-success" data-testid="request-success">
                  <CheckCircle2 size={28} />
                  <div className="lp-request-success-title">Request received</div>
                  <p>
                    Our team reviews each request and creates a verified
                    candidate login. You will receive credentials by phone
                    or email shortly.
                  </p>
                </div>
              ) : (
                <form onSubmit={submit} className="lp-form">
                  <div className="lp-form-row">
                    <Field label="Full name *">
                      <input
                        required
                        data-testid="request-name"
                        value={form.name}
                        onChange={(e) => setForm({ ...form, name: e.target.value })}
                        className="lp-input"
                      />
                    </Field>
                    <Field label="Party / Org">
                      <input
                        data-testid="request-party"
                        value={form.party}
                        onChange={(e) => setForm({ ...form, party: e.target.value })}
                        className="lp-input"
                      />
                    </Field>
                  </div>
                  <div className="lp-form-row">
                    <Field label="Phone *">
                      <input
                        required
                        type="tel"
                        data-testid="request-phone"
                        value={form.phone}
                        onChange={(e) => setForm({ ...form, phone: e.target.value })}
                        className="lp-input"
                      />
                    </Field>
                    <Field label="Email *">
                      <input
                        required
                        type="email"
                        data-testid="request-email"
                        value={form.email}
                        onChange={(e) => setForm({ ...form, email: e.target.value })}
                        className="lp-input"
                      />
                    </Field>
                  </div>
                  <div>
                    <label className="lp-label">Wards of interest</label>
                    <div className="lp-wardpills">
                      {wards.map((w) => (
                        <button
                          type="button"
                          key={w.id}
                          data-testid={`request-ward-${w.name.toLowerCase()}`}
                          onClick={() => toggleWard(w.id)}
                          className={`lp-wardpill ${form.wards.includes(w.id) ? "is-on" : ""}`}
                        >
                          {w.name}
                        </button>
                      ))}
                    </div>
                  </div>
                  <Field label="Message (optional)">
                    <textarea
                      data-testid="request-message"
                      rows={3}
                      value={form.message}
                      onChange={(e) => setForm({ ...form, message: e.target.value })}
                      className="lp-input lp-input-textarea"
                    />
                  </Field>
                  <button
                    type="submit"
                    disabled={submitting}
                    data-testid="request-submit-btn"
                    className="lp-btn lp-btn-saffron lp-btn-block"
                  >
                    <Send size={14} />
                    {submitting ? "Submitting…" : "Submit request"}
                  </button>
                </form>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="lp-footer">
        <div className="lp-container lp-footer-inner">
          <div className="lp-brand">
            <span className="lp-brand-icon">
              <Vote size={14} />
            </span>
            <span className="lp-brand-text">
              Ward<span className="lp-brand-mono">Intel</span>
            </span>
          </div>
          <div className="lp-footer-meta">
            <span>WardIntel © {new Date().getFullYear()}</span>
            <span className="lp-trust-sep" />
            <span>Built for BBMP ward election intelligence</span>
            <span className="lp-trust-sep" />
            <span><Sun size={11} /> Bengaluru</span>
          </div>
        </div>
      </footer>
    </div>
  );
}

function Stat({ label, value, tone }) {
  return (
    <div className={`lp-wardcard-stat ${tone === "ok" ? "is-ok" : ""}`}>
      <span className="lp-wardcard-stat-l">{label}</span>
      <span className="lp-wardcard-stat-v">{value}</span>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="lp-field">
      <span className="lp-label">{label}</span>
      {children}
    </label>
  );
}

const styles = `
.lp-root{ font-family:"IBM Plex Sans",system-ui,sans-serif; color:#0b1220; background:#fffaf3; }
.lp-container{ max-width:1240px; margin:0 auto; padding:0 1.25rem; }
@media(min-width:768px){ .lp-container{ padding:0 2rem; } }
.lp-section{ padding:5rem 0; }
@media(min-width:768px){ .lp-section{ padding:7rem 0; } }
.lp-section-header{ max-width:780px; margin-bottom:3rem; }
.lp-section-header-inverse{ color:#fffaf3; }
.lp-h1{ font-size:clamp(2.5rem,6vw,5rem); line-height:0.98; letter-spacing:-0.035em; font-weight:700; margin:1.5rem 0 1.25rem; color:#0b1220; }
.lp-h1 em{ font-style:normal; color:#0b1220; position:relative; }
.lp-h1 em::after{ content:""; position:absolute; left:0; right:0; bottom:0.08em; height:0.18em; background:#f59e0b; z-index:-1; opacity:0.55; }
.lp-h1-accent{ color:#0b1220; background:linear-gradient(0deg,#f59e0b 30%,transparent 30%); padding:0 0.1em; }
.lp-h2{ font-size:clamp(2rem,4vw,3.5rem); line-height:1.02; letter-spacing:-0.03em; font-weight:600; margin:1rem 0 1rem; }
.lp-h2-inverse{ color:#fffaf3; }
.lp-h2-strike{ position:relative; display:inline-block; opacity:0.45; }
.lp-h2-strike::after{ content:""; position:absolute; left:-4%; right:-4%; top:55%; height:3px; background:#f59e0b; transform:rotate(-3deg); }
.lp-saffron{ color:#f59e0b; }
.lp-saffron-dark{ color:#c2410c; }
.lp-lead{ font-size:1.125rem; line-height:1.55; color:#1f2937; max-width:560px; margin:0; }
.lp-lead-inverse{ color:#cbd5e1; max-width:520px; margin-top:0.75rem; font-size:1rem; line-height:1.6; }
.lp-lead-muted{ color:#475569; }

/* HEADER */
.lp-header{ position:sticky; top:0; z-index:50; backdrop-filter:blur(14px); background:rgba(255,250,243,0.78); border-bottom:1px solid rgba(11,18,32,0.08); }
.lp-header-inner{ display:flex; align-items:center; justify-content:space-between; padding:1rem 1.25rem; gap:1rem; }
@media(min-width:768px){ .lp-header-inner{ padding:1rem 2rem; } }
.lp-brand{ display:flex; align-items:center; gap:0.6rem; font-weight:600; font-size:1.05rem; text-decoration:none; color:inherit; }
.lp-brand-icon{ width:34px; height:34px; border-radius:8px; background:#0b1220; color:#f59e0b; display:flex; align-items:center; justify-content:center; }
.lp-brand-mono{ font-family:"IBM Plex Mono",monospace; font-weight:500; }
.lp-nav{ display:none; gap:1.75rem; font-size:0.875rem; color:#1f2937; }
@media(min-width:900px){ .lp-nav{ display:flex; } }
.lp-nav a{ text-decoration:none; color:inherit; opacity:0.7; transition:opacity 0.2s; }
.lp-nav a:hover{ opacity:1; }
.lp-header-cta{ display:flex; align-items:center; gap:0.6rem; }
.lp-link{ text-decoration:none; color:#0b1220; font-size:0.875rem; font-weight:500; padding:0.5rem 0.75rem; border-radius:8px; }
.lp-link:hover{ background:rgba(11,18,32,0.04); }

/* BUTTONS */
.lp-btn{ display:inline-flex; align-items:center; gap:0.5rem; padding:0.65rem 1rem; border-radius:8px; font-weight:500; font-size:0.875rem; text-decoration:none; border:1px solid transparent; cursor:pointer; transition:all 0.2s ease; }
.lp-btn-saffron{ background:#f59e0b; color:#0b1220; }
.lp-btn-saffron:hover{ background:#d97706; transform:translateY(-1px); box-shadow:0 6px 18px -6px rgba(245,158,11,0.55); }
.lp-btn-ink{ background:#0b1220; color:#fffaf3; }
.lp-btn-ink:hover{ background:#1e293b; transform:translateY(-1px); }
.lp-btn-ghost{ background:transparent; color:#0b1220; border-color:rgba(11,18,32,0.18); }
.lp-btn-ghost:hover{ background:rgba(11,18,32,0.04); }
.lp-btn-block{ width:100%; justify-content:center; padding:0.85rem 1.25rem; font-weight:600; }

/* HERO */
.lp-hero{ position:relative; padding:5rem 0 0; overflow:hidden; }
@media(min-width:1024px){ .lp-hero{ padding:7rem 0 0; } }
.lp-hero-grain{ position:absolute; inset:0; background-image:radial-gradient(circle at 1px 1px,rgba(11,18,32,0.06) 1px,transparent 0); background-size:18px 18px; opacity:0.5; pointer-events:none; }
.lp-hero-inner{ position:relative; display:grid; grid-template-columns:1fr; gap:3.5rem; }
@media(min-width:1024px){ .lp-hero-inner{ grid-template-columns:1.15fr 1fr; gap:3rem; align-items:center; } }
.lp-eyebrow{ display:inline-flex; align-items:center; gap:0.5rem; font-family:"IBM Plex Mono",monospace; font-size:0.7rem; letter-spacing:0.2em; text-transform:uppercase; color:#64748b; font-weight:500; padding:0.4rem 0.7rem; border:1px solid rgba(11,18,32,0.12); border-radius:999px; background:rgba(255,250,243,0.5); }
.lp-eyebrow-ink{ background:#0b1220; color:#f59e0b; border-color:#0b1220; }
.lp-eyebrow-saffron{ background:#f59e0b; color:#0b1220; border-color:#f59e0b; }
.lp-dot{ width:7px; height:7px; background:#16a34a; border-radius:50%; box-shadow:0 0 0 3px rgba(22,163,74,0.18); animation:lp-pulse 2s infinite; }
@keyframes lp-pulse{ 0%,100%{ opacity:1; } 50%{ opacity:0.5; } }
.lp-cta-row{ display:flex; gap:0.75rem; margin:2rem 0 1.5rem; flex-wrap:wrap; }
.lp-trust{ display:flex; flex-wrap:wrap; gap:0.75rem; align-items:center; font-family:"IBM Plex Mono",monospace; font-size:0.72rem; letter-spacing:0.06em; text-transform:uppercase; color:#475569; }
.lp-trust-sep{ width:3px; height:3px; background:#cbd5e1; border-radius:50%; }

/* STAT GRID — hero right */
.lp-hero-right{ position:relative; }
.lp-stat-grid{ display:grid; grid-template-columns:1fr 1fr; grid-template-rows:auto auto; gap:0.75rem; }
.lp-stat{ position:relative; background:#fff; border:1px solid rgba(11,18,32,0.1); border-radius:14px; padding:1.25rem 1.25rem 1.5rem; overflow:hidden; transition:transform 0.2s; }
.lp-stat:hover{ transform:translateY(-2px); }
.lp-stat-big{ grid-column:1 / span 2; background:#0b1220; color:#fffaf3; border-color:#0b1220; }
.lp-stat-big .lp-stat-label{ color:#f59e0b; }
.lp-stat-big .lp-stat-value{ color:#fff; font-size:3.5rem; }
.lp-stat-big .lp-stat-icon{ color:#f59e0b; }
.lp-stat-label{ font-family:"IBM Plex Mono",monospace; font-size:0.65rem; letter-spacing:0.2em; text-transform:uppercase; color:#64748b; font-weight:500; }
.lp-stat-value{ font-family:"IBM Plex Mono",monospace; font-size:2rem; font-weight:700; letter-spacing:-0.04em; margin-top:0.5rem; color:#0b1220; line-height:1; }
.lp-stat-icon{ position:absolute; bottom:0.9rem; right:1rem; color:#cbd5e1; }
.lp-hero-quote{ margin-top:1rem; padding:0.9rem 1.1rem; border-left:3px solid #f59e0b; background:rgba(245,158,11,0.06); border-radius:0 8px 8px 0; font-size:0.85rem; line-height:1.5; color:#1f2937; display:flex; gap:0.6rem; align-items:flex-start; }
.lp-hero-quote svg{ color:#f59e0b; flex-shrink:0; margin-top:2px; }

/* MARQUEE */
.lp-marquee{ margin-top:4rem; padding:1rem 0; background:#0b1220; overflow:hidden; border-top:1px solid rgba(245,158,11,0.18); border-bottom:1px solid rgba(245,158,11,0.18); }
.lp-marquee-track{ display:flex; gap:2.5rem; white-space:nowrap; animation:lp-scroll 40s linear infinite; }
.lp-marquee-item{ display:inline-flex; align-items:center; gap:0.45rem; font-family:"IBM Plex Mono",monospace; font-size:0.78rem; letter-spacing:0.12em; text-transform:uppercase; color:#cbd5e1; }
.lp-marquee-item svg{ color:#f59e0b; }
@keyframes lp-scroll{ from{ transform:translateX(0); } to{ transform:translateX(-50%); } }

/* CREAM SECTIONS */
.lp-cream{ background:#fffaf3; }
.lp-three-cols{ display:grid; grid-template-columns:1fr; gap:1.25rem; }
@media(min-width:900px){ .lp-three-cols{ grid-template-columns:repeat(3,1fr); } }
.lp-howcard{ position:relative; padding:2rem; border-radius:18px; background:#fff; border:1px solid rgba(11,18,32,0.08); overflow:hidden; transition:transform 0.3s ease; }
.lp-howcard:hover{ transform:translateY(-4px); }
.lp-howcard-tag{ font-family:"IBM Plex Mono",monospace; font-size:0.7rem; letter-spacing:0.18em; text-transform:uppercase; color:#64748b; font-weight:500; }
.lp-howcard-icon{ margin-top:1.25rem; color:#0b1220; }
.lp-howcard-title{ font-size:1.5rem; font-weight:600; letter-spacing:-0.02em; margin:1rem 0 0.65rem; color:#0b1220; }
.lp-howcard-body{ font-size:0.95rem; line-height:1.55; color:#475569; margin:0; }
.lp-howcard.lp-acc-saffron{ background:linear-gradient(180deg,#fef3c7 0%,#fff 100%); border-color:#fde68a; }
.lp-howcard.lp-acc-saffron .lp-howcard-icon{ color:#d97706; }
.lp-howcard.lp-acc-ink{ background:#0b1220; border-color:#0b1220; }
.lp-howcard.lp-acc-ink .lp-howcard-tag{ color:#f59e0b; }
.lp-howcard.lp-acc-ink .lp-howcard-title{ color:#fffaf3; }
.lp-howcard.lp-acc-ink .lp-howcard-body{ color:#94a3b8; }
.lp-howcard.lp-acc-ink .lp-howcard-icon{ color:#f59e0b; }
.lp-howcard.lp-acc-blue{ background:linear-gradient(180deg,#dbeafe 0%,#fff 100%); border-color:#bfdbfe; }
.lp-howcard.lp-acc-blue .lp-howcard-icon{ color:#1d4ed8; }

/* INK SECTION (wards + request) */
.lp-ink{ background:#0b1220; color:#fffaf3; position:relative; }
.lp-ink::before{ content:""; position:absolute; inset:0; background-image:radial-gradient(circle at 1px 1px,rgba(245,158,11,0.06) 1px,transparent 0); background-size:24px 24px; opacity:0.6; pointer-events:none; }
.lp-empty{ color:#94a3b8; font-size:0.875rem; }

/* WARDS */
.lp-wards-grid{ display:grid; grid-template-columns:1fr; gap:1rem; }
@media(min-width:700px){ .lp-wards-grid{ grid-template-columns:repeat(2,1fr); } }
@media(min-width:1000px){ .lp-wards-grid{ grid-template-columns:repeat(3,1fr); } }
.lp-wardcard{ position:relative; background:#111c30; border:1px solid #1e293b; border-radius:18px; padding:1.5rem; transition:all 0.25s ease; overflow:hidden; }
.lp-wardcard::before{ content:""; position:absolute; top:0; left:0; right:0; height:3px; background:linear-gradient(90deg,#f59e0b,#1d4ed8); opacity:0; transition:opacity 0.3s; }
.lp-wardcard:hover{ transform:translateY(-3px); border-color:#f59e0b; background:#1a263f; }
.lp-wardcard:hover::before{ opacity:1; }
.lp-wardcard-top{ display:flex; justify-content:space-between; align-items:flex-start; }
.lp-wardcard-num{ font-family:"IBM Plex Mono",monospace; font-size:0.68rem; letter-spacing:0.18em; text-transform:uppercase; color:#f59e0b; font-weight:500; }
.lp-wardcard-name{ font-size:1.5rem; font-weight:600; letter-spacing:-0.02em; margin:0.3rem 0 0.25rem; color:#fffaf3; }
.lp-wardcard-district{ display:inline-flex; align-items:center; gap:0.3rem; font-size:0.78rem; color:#94a3b8; }
.lp-wardcard-index{ font-family:"IBM Plex Mono",monospace; font-size:2.5rem; font-weight:700; color:#1e293b; letter-spacing:-0.04em; line-height:1; }
.lp-wardcard-stats{ display:grid; grid-template-columns:repeat(3,1fr); gap:0.5rem; margin:1.5rem 0 1.25rem; padding-top:1.25rem; border-top:1px solid #1e293b; }
.lp-wardcard-stat{ display:flex; flex-direction:column; gap:0.2rem; }
.lp-wardcard-stat-l{ font-family:"IBM Plex Mono",monospace; font-size:0.6rem; letter-spacing:0.18em; text-transform:uppercase; color:#64748b; }
.lp-wardcard-stat-v{ font-family:"IBM Plex Mono",monospace; font-size:1.25rem; font-weight:600; color:#fffaf3; letter-spacing:-0.02em; }
.lp-wardcard-stat.is-ok .lp-wardcard-stat-v{ color:#16a34a; font-size:0.95rem; padding-top:4px; }
.lp-wardcard-cta{ display:flex; align-items:center; justify-content:center; gap:0.4rem; width:100%; padding:0.75rem; background:#fffaf3; color:#0b1220; border-radius:10px; font-weight:500; font-size:0.875rem; text-decoration:none; transition:all 0.2s; }
.lp-wardcard-cta:hover{ background:#f59e0b; gap:0.6rem; }
.lp-wardcard-arrow{ transition:transform 0.2s; }
.lp-wardcard-cta:hover .lp-wardcard-arrow{ transform:translateX(2px); }

/* WHY GRID */
.lp-why-grid{ display:grid; grid-template-columns:1fr; gap:0.75rem; }
@media(min-width:768px){ .lp-why-grid{ grid-template-columns:1fr 1fr; gap:1rem; } }
.lp-whycard{ display:flex; gap:1.5rem; padding:1.75rem; background:#fff; border:1px solid rgba(11,18,32,0.08); border-radius:14px; transition:all 0.25s; }
.lp-whycard:hover{ background:#0b1220; color:#fffaf3; transform:translateY(-2px); border-color:#0b1220; }
.lp-whycard:hover .lp-whycard-num{ color:#f59e0b; }
.lp-whycard:hover .lp-whycard-title{ color:#fffaf3; }
.lp-whycard:hover .lp-whycard-text{ color:#94a3b8; }
.lp-whycard-num{ font-family:"IBM Plex Mono",monospace; font-size:1.5rem; font-weight:700; color:#0b1220; letter-spacing:-0.04em; line-height:1; transition:color 0.25s; }
.lp-whycard-title{ font-size:1.15rem; font-weight:600; margin:0 0 0.4rem; color:#0b1220; transition:color 0.25s; }
.lp-whycard-text{ font-size:0.9rem; line-height:1.55; color:#475569; margin:0; transition:color 0.25s; }

/* REQUEST FORM */
.lp-request-wrap{ display:grid; grid-template-columns:1fr; gap:3rem; }
@media(min-width:1024px){ .lp-request-wrap{ grid-template-columns:1fr 1fr; gap:5rem; align-items:start; } }
.lp-request-list{ list-style:none; padding:0; margin:1.5rem 0 0; display:flex; flex-direction:column; gap:0.6rem; color:#cbd5e1; font-size:0.9rem; }
.lp-request-list li{ display:flex; align-items:center; gap:0.6rem; }
.lp-request-list svg{ color:#16a34a; flex-shrink:0; }
.lp-request-success{ padding:3rem 2rem; background:#fff; border-radius:18px; color:#0b1220; text-align:center; }
.lp-request-success svg{ color:#16a34a; }
.lp-request-success-title{ font-size:1.5rem; font-weight:600; margin:1rem 0 0.5rem; }
.lp-request-success p{ color:#475569; line-height:1.55; margin:0; }
.lp-form{ background:#fff; padding:2rem; border-radius:18px; color:#0b1220; display:flex; flex-direction:column; gap:1.1rem; }
.lp-form-row{ display:grid; grid-template-columns:1fr; gap:1.1rem; }
@media(min-width:600px){ .lp-form-row{ grid-template-columns:1fr 1fr; } }
.lp-field{ display:block; }
.lp-label{ display:block; font-size:0.78rem; font-weight:600; color:#1f2937; margin-bottom:0.45rem; text-transform:uppercase; letter-spacing:0.08em; font-family:"IBM Plex Mono",monospace; }
.lp-input{ width:100%; box-sizing:border-box; border:1.5px solid #e2e8f0; border-radius:10px; padding:0.7rem 0.85rem; font-size:0.9rem; color:#0b1220; background:#fff; font-family:inherit; transition:border-color 0.2s; }
.lp-input:focus{ outline:none; border-color:#f59e0b; box-shadow:0 0 0 3px rgba(245,158,11,0.18); }
.lp-input-textarea{ resize:vertical; min-height:80px; }
.lp-wardpills{ display:flex; flex-wrap:wrap; gap:0.4rem; }
.lp-wardpill{ padding:0.45rem 0.85rem; font-size:0.78rem; font-weight:500; border-radius:999px; border:1.5px solid #e2e8f0; background:#fff; color:#1f2937; cursor:pointer; transition:all 0.15s; }
.lp-wardpill:hover{ border-color:#f59e0b; }
.lp-wardpill.is-on{ background:#0b1220; color:#f59e0b; border-color:#0b1220; }

/* FOOTER */
.lp-footer{ background:#0b1220; color:#94a3b8; padding:2rem 0; border-top:1px solid #1e293b; }
.lp-footer-inner{ display:flex; flex-direction:column; gap:1rem; align-items:center; justify-content:space-between; }
@media(min-width:768px){ .lp-footer-inner{ flex-direction:row; } }
.lp-footer .lp-brand-text{ color:#fffaf3; }
.lp-footer-meta{ display:flex; flex-wrap:wrap; align-items:center; gap:0.75rem; font-family:"IBM Plex Mono",monospace; font-size:0.72rem; letter-spacing:0.08em; text-transform:uppercase; }
.lp-footer-meta svg{ vertical-align:middle; margin-right:3px; color:#f59e0b; }
`;
